import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { type IBrand } from '@mahawi/eshop-common/src/types';
import { Button, Col, Divider, Form, Input, Row, Space } from 'antd';
import { type ColumnType } from 'antd/es/table/interface';
import React, { Key, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Link from '../components/link';
import Table from '../components/table';
import UpdatedAt from '../components/updated-at';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { brandAdd, brandsLoadAll } from '../reducers/brand/reducer';
import { SPACING } from '../theme/tokens';

interface DataType {
  key: string;
  name: string;
}

interface IForm {
  name: string;
}

function BrandContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const brandBrands = useSelector((s: RootState) => s.Brand.brands);
  const brandInProcess = useSelector((s: RootState) => s.Brand.inProcess);
  const brandUpdatedAt = useSelector((s: RootState) => s.Brand.updatedAt);

  const [form] = Form.useForm<IForm>();

  const onFinish = useCallback(
    ({ name }: IForm): void => {
      dispatch(brandAdd(name.trim()));
    },
    [dispatch],
  );

  const dataSource = useMemo<DataType[]>(
    () =>
      brandBrands?.map((brand: IBrand): DataType => ({
        key: brand.uuid,
        name: brand.name,
      })) ?? [],
    [brandBrands],
  );

  const columns = useMemo<ColumnType<object>[]>(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.name, b.name),
        filterSearch: true,
        filters: [
          ...new Set(dataSource.map(({ name }: DataType): string => name)),
        ]
          .map((name: string) => ({ text: name, value: name }))
          .sort((a, b) => stringCompare(a.text, b.text)),
        onFilter: (value: boolean | Key, record: DataType): boolean =>
          record.name === value,
        render: (name: string, record: DataType): React.ReactElement => (
          <Link to={`/brand/${record.key}`} label={name} />
        ),
        width: '100%',
      },
    ],
    [dataSource],
  );

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <Space
        size={SPACING.lg}
        align="center"
        separator={<Divider orientation="vertical" />}
      >
        <Button
          type={!brandBrands ? 'primary' : 'default'}
          onClick={(): void => {
            dispatch(brandsLoadAll());
          }}
          disabled={brandInProcess}
        >
          Reload all <ReloadOutlined />
        </Button>

        <UpdatedAt value={brandUpdatedAt} />
      </Space>

      <Form form={form} onFinish={onFinish}>
        <Row gutter={[SPACING.none, SPACING.xl]}>
          <Col span={8} offset={8} className="gutter-row">
            <Form.Item
              name="name"
              rules={[
                {
                  required: true,
                },
                {
                  type: 'string',
                  min: 2,
                },
              ]}
            >
              <Input
                size="large"
                placeholder="Create new brand name"
                prefix={<EditOutlined />}
                type="string"
                disabled={brandInProcess}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={brandInProcess}
        emptyText="No brands found"
      />
    </Space>
  );
}

export default BrandContainer;
